/**
 * `createBrowserRouter` in DATA MODE — adopted for its router shape: nested
 * layout routes, per-route `errorElement`, typed params.
 *
 * NOT as a second data layer. No `loader` in this repo performs a data read:
 * a fetching loader would create a cache TanStack Query does not know about,
 * and the two would drift on every mutation — a class of bug that reproduces
 * only on the second visit to a route. Loaders are used only where a route
 * must validate a param before painting.
 *
 * This file holds COMPOSITION, never product code. A route file resolves
 * params and renders the feature's page component; past a handful of lines,
 * product logic has leaked and belongs back in `src/features/`.
 */
import { createBrowserRouter } from 'react-router-dom';

import { AppShell, AuthShell } from '@shared/app-shell';

import { AuthGuard, BootGate, GuestGuard } from './guards';
import { RootErrorFallback } from './RootErrorFallback';
import { appShellRoutes, authShellRoutes, systemShellRoutes } from './routes';

export const router = createBrowserRouter([
  {
    // The guard wraps the authenticated LAYOUT route — not each page, and not
    // a per-page effect.
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    errorElement: <RootErrorFallback />,
    children: appShellRoutes,
  },
  {
    // The auth half-shell is UNGUARDED for an anonymous reader and CLOSED for a
    // signed-in one. `GuestGuard` is the reverse of `AuthGuard`: it never asks
    // for a session, it refuses one — a reader who already holds a session is
    // sent to their `next` rather than shown a form that would mint a second
    // session over a live one.
    element: (
      <GuestGuard>
        <AuthShell />
      </GuestGuard>
    ),
    errorElement: <RootErrorFallback />,
    children: authShellRoutes,
  },
  {
    // The system pages take the SAME shell and NO GUARD — a reader on a dead
    // address is more lost than anywhere else, and the shell is the only route
    // out this area owns. They do wait for the boot refresh, though: the shell
    // they render inside mounts the notification handle, which reads on mount,
    // and without the wait that read left a 401 in the console on every entry.
    // `BootGate` is the wait alone — it never redirects and never requires a
    // session.
    element: (
      <BootGate>
        <AppShell />
      </BootGate>
    ),
    errorElement: <RootErrorFallback />,
    children: systemShellRoutes,
  },
]);
